import React, { useMemo, useState } from 'react';
import { ESTILOS, ASPECTOS, estiloPorClave } from '../ai/estilos';
import { generarImagen, consultarEstado, cancelarGeneracion } from '../services/iaImagenService';
import { useToast } from '../context/ToastContext';
import { mensajeAmigable } from '../utils/errores';
import CreditosIABadge from './CreditosIABadge';

// Mejora una foto SUELTA (al crear un producto nuevo, antes de guardarlo).
// Genera con IA sobre la imagen ya subida y, si te gusta, la usas como foto
// del producto. La original sigue siendo tuya (la que subiste).
export default function MejorarFotoNuevaModal({ imagenUrl, datosProducto, onUsar, onCerrar, agregadas = 0, maxFotos = 5 }) {
  const { mostrarToast } = useToast();
  const [estilo, setEstilo] = useState(null);
  const [instrucciones, setInstrucciones] = useState('');
  const [aspecto, setAspecto] = useState('1:1');
  const [generando, setGenerando] = useState(false);
  const [resultado, setResultado] = useState(null); // { resultado_url }
  const [refrescarCreditos, setRefrescarCreditos] = useState(0);
  const esPersonalizada = estilo === 'personalizada';
  const puedeGenerar = !!estilo && !generando && (!esPersonalizada || instrucciones.trim().length > 0);

  const P = 'var(--color-primario,#DB2777)';
  const seccion = { padding: '16px 20px', borderBottom: '1px solid #F3F4F6' };
  const esperarResultado = useMemo(() => async (id) => {
    if (!id) return { estado: 'failed', error_mensaje: 'No se pudo iniciar la generación.' };
    for (let i = 0; i < 48; i++) { // ~4 min: da margen cuando el proveedor está lento
      await new Promise((r) => setTimeout(r, 5000));
      let d; try { d = await consultarEstado(id); } catch { continue; }
      const g = d && d.generacion;
      if (g && g.estado === 'completed' && g.resultado_url) return g;
      if ((g && g.estado === 'failed') || (d && d.error && !d.procesando)) return { estado: 'failed', error_mensaje: (d && d.mensaje) || 'No se pudo generar la imagen.' };
    }
    // Se agotó la espera: el servidor no reembolsa mientras el proveedor sigue
    // trabajando (la tarea ya se está cobrando). Foto suelta = sin historial.
    await cancelarGeneracion(id);
    return { estado: 'failed', error_mensaje: 'Tu foto está tardando más de lo normal. Intenta de nuevo en unos minutos.' };
  }, []);

  const elegirEstilo = (clave) => { setEstilo(clave); const e = estiloPorClave(clave); if (e) setAspecto(e.aspecto); };

  const generar = async () => {
    if (!puedeGenerar) { if (esPersonalizada && !instrucciones.trim()) mostrarToast('Escribe cómo quieres la foto.', 'error'); return; }
    setGenerando(true); setResultado(null);
    try {
      const gen = await generarImagen({ imagenUrl, datosProducto, estilo, instrucciones: instrucciones.trim() || null, aspecto });
      const listo = (gen && gen.estado === 'completed' && gen.resultado_url) ? gen : await esperarResultado(gen && gen.id);
      if (listo && listo.estado === 'completed' && listo.resultado_url) {
        setResultado(listo);
        mostrarToast('¡Foto lista! Mira cómo quedó 👇', 'exito');
      } else {
        mostrarToast(listo?.error_mensaje || 'No se pudo generar la imagen. Prueba de nuevo.', 'error', 6000);
      }
    } catch (e) {
      mostrarToast(e.code ? e.message : mensajeAmigable(e), 'error', 6000);
    } finally { setGenerando(false); setRefrescarCreditos((x) => x + 1); }
  };

  const descargar = async () => {
    if (!resultado?.resultado_url) return;
    try {
      const blob = await (await fetch(resultado.resultado_url)).blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'foto-ia.png';
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 4000);
      mostrarToast('Descargando la imagen 📥', 'exito');
    } catch { window.open(resultado.resultado_url, '_blank', 'noopener,noreferrer'); }
  };

  const card = { background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '620px', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: '14px' }} onClick={onCerrar}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={{ ...seccion, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '19px', color: P }}>✨ Crear foto profesional</h2>
            <p style={{ margin: '2px 0 0', fontSize: '12.5px', color: '#9CA3AF' }}>
              {agregadas > 0 ? `Ya agregaste ${agregadas} foto(s). Puedes crear otra o cerrar.` : 'Mejorá tu foto antes de guardar el producto.'}
            </p>
            <div><CreditosIABadge refrescar={refrescarCreditos} /></div>
          </div>
          <button onClick={onCerrar} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#9CA3AF' }}>✕</button>
        </div>

        {resultado && resultado.resultado_url ? (
          <div style={seccion}>
            <p style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: 600, color: '#374151' }}>Comparación</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <Fig etiqueta="Antes" url={imagenUrl} P={P} />
              <Fig etiqueta="Después" url={resultado.resultado_url} destacado P={P} />
            </div>
            <button onClick={() => { onUsar(resultado.resultado_url); setResultado(null); }} disabled={agregadas >= maxFotos}
              style={{ width: '100%', marginTop: '12px', padding: '13px', borderRadius: '9px', border: 'none', background: agregadas >= maxFotos ? '#C7C2D6' : P, color: '#fff', fontSize: '14.5px', fontWeight: 700, cursor: agregadas >= maxFotos ? 'not-allowed' : 'pointer' }}>
              {agregadas >= maxFotos ? 'Ya tienes 5 fotos' : '✅ Usar esta foto (y crear otra)'}
            </button>
            <button onClick={descargar}
              style={{ width: '100%', marginTop: '8px', padding: '11px', borderRadius: '9px', border: '1px solid #D1D5DB', background: '#fff', color: '#374151', fontSize: '13.5px', fontWeight: 700, cursor: 'pointer' }}>
              📥 Descargar imagen
            </button>
            <button onClick={() => setResultado(null)}
              style={{ width: '100%', marginTop: '8px', padding: '10px', borderRadius: '9px', border: '1px solid #E5E7EB', background: '#fff', color: '#374151', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
              ↻ Generar otra
            </button>
          </div>
        ) : (
          <>
            <div style={seccion}>
              <p style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: 600, color: '#374151' }}>¿Qué tipo de foto quieres crear?</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' }}>
                {ESTILOS.map((e) => (
                  <button key={e.clave} onClick={() => elegirEstilo(e.clave)}
                    style={{ textAlign: 'left', padding: '11px', borderRadius: '12px', cursor: 'pointer', border: estilo === e.clave ? `2px solid ${P}` : '1px solid #E5E7EB', background: estilo === e.clave ? 'color-mix(in srgb, var(--color-primario,#DB2777) 8%, white)' : '#fff' }}>
                    <div style={{ fontSize: '20px', lineHeight: 1 }}>{e.icono}</div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#111827', margin: '6px 0 2px' }}>{e.nombre}</div>
                    <div style={{ fontSize: '11.5px', color: '#6B7280', lineHeight: 1.35 }}>{e.desc}</div>
                  </button>
                ))}
              </div>
            </div>
            <div style={seccion}>
              <p style={{ margin: '0 0 6px', fontSize: '13px', fontWeight: 600, color: '#374151' }}>{esPersonalizada ? '¿Cómo quieres la foto? *' : 'Instrucciones extra (opcional)'}</p>
              <textarea value={instrucciones} onChange={(e) => setInstrucciones(e.target.value)} rows={2} maxLength={500}
                placeholder={esPersonalizada ? 'Ej. sobre una mesa de madera…' : 'Ej. fondo más claro, estilo elegante…'}
                style={{ width: '100%', padding: '10px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '13.5px', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
              <p style={{ margin: '12px 0 6px', fontSize: '13px', fontWeight: 600, color: '#374151' }}>Formato</p>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {ASPECTOS.map((a) => (
                  <button key={a.valor} onClick={() => setAspecto(a.valor)} title={a.para}
                    style={{ padding: '6px 11px', borderRadius: '999px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, border: aspecto === a.valor ? `2px solid ${P}` : '1px solid #E5E7EB', background: aspecto === a.valor ? 'color-mix(in srgb, var(--color-primario,#DB2777) 8%, white)' : '#fff', color: aspecto === a.valor ? P : '#6B7280' }}>{a.nombre}</button>
                ))}
              </div>
            </div>
            <div style={{ ...seccion, borderBottom: 'none' }}>
              <button onClick={generar} disabled={!puedeGenerar}
                style={{ width: '100%', padding: '14px', borderRadius: '10px', border: 'none', fontSize: '15px', fontWeight: 'bold', color: '#fff', background: puedeGenerar ? P : '#C7C2D6', cursor: puedeGenerar ? 'pointer' : 'not-allowed' }}>
                {generando ? '⏳ Creando tu foto…' : (agregadas > 0 ? '✨ Crear otra foto' : '✨ Crear foto profesional')}
              </button>
              {agregadas > 0 && !generando && (
                <button onClick={onCerrar}
                  style={{ width: '100%', marginTop: '8px', padding: '11px', borderRadius: '9px', border: '1px solid #E5E7EB', background: '#fff', color: '#374151', fontSize: '13.5px', fontWeight: 700, cursor: 'pointer' }}>
                  ✔️ Listo ({agregadas} foto{agregadas === 1 ? '' : 's'} agregada{agregadas === 1 ? '' : 's'})
                </button>
              )}
              {generando && <p style={{ margin: '10px 0 0', fontSize: '12.5px', color: '#9CA3AF', textAlign: 'center' }}>La IA está trabajando con tu foto. Puede tardar de 1 a 3 minutos cuando hay mucha demanda. No cierres esta ventana…</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Fig({ etiqueta, url, destacado, P }) {
  return (
    <div>
      <div style={{ fontSize: '11px', fontWeight: 700, color: destacado ? P : '#9CA3AF', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '.04em' }}>{etiqueta}</div>
      <div style={{ border: destacado ? `2px solid ${P}` : '1px solid #E5E7EB', borderRadius: '10px', overflow: 'hidden', height: '180px', background: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img src={url} alt={etiqueta} style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#fff' }} />
      </div>
    </div>
  );
}
